import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useCanManageTemplates } from '@/hooks/usePermissions';
import { StandardsFilters } from '@/pages/admin/templates/[id]/component/standards-filters';
import { StandardsTable } from '@/pages/admin/templates/[id]/component/enhanced-standards-table';
import { ColumnInfoDialog } from '@/pages/admin/templates/[id]/component/column-info-dialog';
import { exportTemplateStandardsExcel } from '@/hooks/queries/useTemplateExport';
import {
  useStandardsQuery,
  useDeleteStandardMutation,
} from '@/hooks/queries/useTemplates';
import type { ApiStandard } from '@/types/templates';

// Type for UI Standard (mapped from ApiStandard)
interface Standard {
  id: string;
  text: string;
  department: string;
  touchpoint: string;
  subtouchpoint: string;
  title: string;
}

export default function TemplateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Permission check
  const canManage = useCanManageTemplates();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<
    number | undefined
  >();
  const [touchpointFilter, setTouchpointFilter] = useState<
    number | undefined
  >();
  const [subTouchpointFilter, setSubTouchpointFilter] = useState<
    number | undefined
  >();
  const [titleFilter, setTitleFilter] = useState<number | undefined>();
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<
    'department' | 'touchpoint' | 'subtouchpoint' | 'title' | null
  >(null);

  // Fetch standards from API using property type ID
  const propertyTypeId = id || '1'; // Default to 1 if no ID
  const {
    data: standardsResponse,
    isPending,
    isFetching,
    error,
  } = useStandardsQuery(propertyTypeId, {
    page: currentPage,
    page_size: 10,
    search: searchQuery,
    department_id: departmentFilter,
    touchpoint_id: touchpointFilter,
    sub_touchpoint_id: subTouchpointFilter,
    title_id: titleFilter,
  });

  const deleteStandardMutation = useDeleteStandardMutation({
    onSuccessCallback: () => {
      // Query will automatically refetch
    },
  });

  // Map API standards to UI format
  const standards: Standard[] = useMemo(() => {
    if (!standardsResponse?.data) return [];

    return standardsResponse.data.map((apiStandard: ApiStandard) => ({
      id: String(apiStandard.id),
      text: apiStandard.question,
      department: apiStandard.department.name,
      touchpoint: apiStandard.touchpoint.name,
      subtouchpoint: apiStandard.sub_touchpoint?.name || '-',
      title: apiStandard.title.name,
    }));
  }, [standardsResponse]);

  // Get property type name from first standard (since all belong to same property type)
  const propertyTypeName = useMemo(() => {
    if (standardsResponse?.data && standardsResponse.data.length > 0) {
      return standardsResponse.data[0].property_type.name;
    }
    return t(
      `templates.admin.propertyTypes.${id?.toLowerCase() || 'hotel'}.name`,
      'Template'
    );
  }, [standardsResponse, id, t]);

  const handleBack = () => {
    navigate('/admin/templates');
  };

  const handleEditStandard = (standard: Standard) => {
    // Navigate to edit standard page with the standard ID in state
    navigate(`/admin/templates/${id}/edit-standards`, { 
      state: { standardId: standard.id } 
    });
  };

  const handleDeleteStandard = (standardId: string) => {
    deleteStandardMutation.mutate(standardId);
  };

  const handleColumnInfo = (columnType: string) => {
    if (
      columnType === 'department' ||
      columnType === 'touchpoint' ||
      columnType === 'subtouchpoint' ||
      columnType === 'title'
    ) {
      setSelectedColumn(columnType);
      setIsColumnDialogOpen(true);
    }
  };

  const handleImportCsv = (file: File) => {
    toast({
      title: t('templates.admin.detail.csvImportStarted'),
      description: t('templates.admin.detail.csvImportMessage', {
        fileName: file.name,
      }),
    });
  };

  const handleExportData = async () => {
    try {
      await exportTemplateStandardsExcel(propertyTypeId, {
        search: searchQuery || undefined,
        department_id: departmentFilter,
        touchpoint_id: touchpointFilter,
        sub_touchpoint_id: subTouchpointFilter,
        title_id: titleFilter,
      });
      
      toast({
        title: t('templates.admin.detail.exportSuccessful'),
        description: t('templates.admin.detail.exportMessage', {
          count: standardsResponse?.pagination.total || 0,
        }),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('templates.admin.detail.exportFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const handleDepartmentChange = (departmentId: number | undefined) => {
    setDepartmentFilter(departmentId);
    setCurrentPage(1);
  };

  const handleTouchpointChange = (touchpointId: number | undefined) => {
    setTouchpointFilter(touchpointId);
    setCurrentPage(1);
  };

  const handleSubTouchpointChange = (subTouchpointId: number | undefined) => {
    setSubTouchpointFilter(subTouchpointId);
    setCurrentPage(1);
  };

  const handleTitleChange = (titleId: number | undefined) => {
    setTitleFilter(titleId);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDepartmentFilter(undefined);
    setTouchpointFilter(undefined);
    setSubTouchpointFilter(undefined);
    setTitleFilter(undefined);
    setCurrentPage(1);
  };

  // Show initial loading state only (not on filter changes)
  // isPending is true only when there's no data at all (first load)
  if (isPending) {
    return (
      <div
        className={cn(
          'min-h-screen bg-background flex items-center justify-center',
          isRTL && 'rtl'
        )}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        className={cn(
          'min-h-screen bg-background flex items-center justify-center',
          isRTL && 'rtl'
        )}
      >
        <div className="text-center">
          <p className="text-destructive">
            {t('common.error', 'An error occurred')}
          </p>
          <p className="text-muted-foreground mt-2">{error.message}</p>
          <Button onClick={() => navigate('/admin/templates')} className="mt-4">
            {t('templates.admin.detail.backToTemplates', 'Back to Templates')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-background', isRTL && 'rtl')}>
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-3 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            {/* Left/Right Side - Back button for LTR, Spacer for RTL */}
            {isRTL ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 ml-1 sm:ml-2 rotate-180" />
                <span className="hidden sm:inline">
                  {t('templates.admin.detail.backToTemplates')}
                </span>
                <span className="sm:hidden">{t('templates.admin.detail.back')}</span>
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">
                  {t('templates.admin.detail.backToTemplates')}
                </span>
                <span className="sm:hidden">{t('templates.admin.detail.back')}</span>
              </Button>
            )}
            
            {/* Title - Centered */}
            <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-center flex-1">
              <span className="hidden sm:inline">
                {isRTL ? (
                  <>
                    {t('templates.admin.detail.standardsManagement', 'Standards Management')}
                   {' '} {propertyTypeName} 
                  </>
                ) : (
                  <>
                    {propertyTypeName}
                  {' '}  {t('templates.admin.detail.standardsManagement', 'Standards Management')}
                  </>
                )}
              </span>
              <span className="sm:hidden">
                {propertyTypeName} {t('templates.admin.detail.standardsManagementMobile')}
              </span>
            </h1>
            
            {/* Right/Left Side - Spacer for balance */}
            <div className="w-[100px] sm:w-[140px] flex-shrink-0"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-3 sm:p-4 lg:p-6">
        <div className="space-y-3 sm:space-y-4 lg:space-y-6">
          <StandardsFilters
            standards={standards}
            propertyTypeId={propertyTypeId}
            searchQuery={searchQuery}
            departmentFilter={departmentFilter}
            touchpointFilter={touchpointFilter}
            subTouchpointFilter={subTouchpointFilter}
            titleFilter={titleFilter}
            onSearchChange={setSearchQuery}
            onDepartmentChange={handleDepartmentChange}
            onTouchpointChange={handleTouchpointChange}
            onSubTouchpointChange={handleSubTouchpointChange}
            onTitleChange={handleTitleChange}
            onClearFilters={handleClearFilters}
            onExportData={canManage ? handleExportData : undefined}
            onSearch={handleSearch}
          />
          <StandardsTable
            standards={standards}
            onEditStandard={canManage ? handleEditStandard : undefined}
            onDeleteStandard={canManage ? handleDeleteStandard : undefined}
            onColumnInfo={handleColumnInfo}
            onImportCsv={canManage ? handleImportCsv : undefined}
            propertyType={propertyTypeName}
            currentPage={currentPage}
            totalPages={standardsResponse?.pagination.last_page || 1}
            totalItems={standardsResponse?.pagination.total || 0}
            onPageChange={setCurrentPage}
            isLoading={isFetching}
          />
        </div>
      </div>

      {/* Dialogs */}
      {selectedColumn && (
        <ColumnInfoDialog
          isOpen={isColumnDialogOpen}
          onClose={() => setIsColumnDialogOpen(false)}
          columnType={selectedColumn}
          propertyTypeId={propertyTypeId}
          propertyTypeName={propertyTypeName}
        />
      )}
    </div>
  );
}
